import { useMutation, useQuery, useQueryClient, UseQueryOptions } from 'react-query'

import { useAccountClient } from '../clients/AccountClient'
import { useConsoleBackendClient } from '../clients/ConsoleBackendClient'
import { useHubSpotClient } from '../clients/HubSpotClient'
import { useTenantClient } from '../clients/TenantClient'
import { useProfile } from '../services/ProfileAndQueryClientProvider'
import {
  GetRolesResponse,
  ListTenantMembersResponse,
  UpdateRoleRequestBody,
  UpdateRoleResponse,
} from '../types/local/consoleBackend'
import {
  MutationKeys,
  QueryKeys,
  reactQueryDefaultOptions,
  ReactQueryMutationHooks,
  useMutationOptions,
} from '../types/local/general'
import {
  GetInvitesResponse,
  InviteStatus,
  InviteUserRequestBody,
  InviteUserResponse,
  ListInvitesResponse,
  RespondToInviteResponse,
  RpcStatus,
} from '../types/local/tenant'

export const useInviteMember = () => {
  const queryClient = useQueryClient()
  const { post } = useTenantClient()
  const { hubspotCreateContact } = useHubSpotClient()
  const { tenant } = useProfile()
  return useMutation(
    ({ ...body }: InviteUserRequestBody) => {
      return post<InviteUserResponse, InviteUserRequestBody>({ path: `profile/invites`, body })
    },
    {
      onSuccess: (data, variables) => {
        queryClient.refetchQueries([QueryKeys.InvitesData])
        try {
          hubspotCreateContact({
            email: variables.email,
            organization: tenant?.name,
          })
        } catch (e) {}
      },
    }
  )
}

export const useInvites = () => {
  const { get } = useTenantClient()
  return useQuery(
    [QueryKeys.InvitesData],
    () => get<GetInvitesResponse>({ path: `profile/invites` }),
    reactQueryDefaultOptions
  )
}

export const useAccountInvites = () => {
  const { get } = useAccountClient()
  return useQuery(
    QueryKeys.AccountInvitesData,
    () => get<ListInvitesResponse>({ path: `account/invites` }),
    reactQueryDefaultOptions
  )
}

export const useAcceptInvite = () => {
  return useChangeInviteStatus('INVITE_STATUS_ACCEPTED')
}

export const useDeclineInvite = () => {
  return useChangeInviteStatus('INVITE_STATUS_DECLINED')
}

export const useCancelInvite = () => {
  return useChangeInviteStatus('INVITE_STATUS_CANCELED')
}

const useChangeInviteStatus = (status: InviteStatus) => {
  const queryClient = useQueryClient()
  const { post } = useTenantClient()
  return useMutation<
    RespondToInviteResponse,
    unknown,
    { inviteId: string; tenantId: string },
    unknown
  >(
    ({ inviteId, tenantId }: { inviteId: string; tenantId: string }) => {
      return post({
        body: { id: inviteId, status },
        headerOverrides: { 'aserto-tenant-id': tenantId },
        path: `profile/invites/${inviteId}`,
      })
    },
    {
      onSuccess: () => {
        queryClient.refetchQueries([QueryKeys.AccountInvitesData, QueryKeys.InvitesData])
      },
    }
  )
}

export const useDeleteTenantMember = () => {
  const { del } = useTenantClient()
  const queryClient = useQueryClient()

  return useMutation(
    ({ id }: { id: string }) => {
      return del({ path: `profile/members/${id}` })
    },
    {
      onSuccess: () => {
        queryClient.removeQueries([QueryKeys.ListTenantMembers])
      },
    }
  )
}

export const useListTenantMembers = (
  options?: Omit<
    UseQueryOptions<ListTenantMembersResponse, RpcStatus, ListTenantMembersResponse, QueryKeys[]>,
    'queryKey' | 'queryFn' | 'retry' | 'staleTime'
  >
) => {
  const { get } = useConsoleBackendClient()
  return useQuery(
    [QueryKeys.ListTenantMembers],
    () =>
      get<ListTenantMembersResponse>({
        path: `list-tenant-members`,
      }),
    {
      ...options,
      enabled: options?.enabled,
      retry: false,
      staleTime: Infinity,
    }
  )
}

export const useUpdateRole = (
  reactQueryHooks: ReactQueryMutationHooks<UpdateRoleResponse, UpdateRoleRequestBody> = {}
) => {
  const mutationOptions = useMutationOptions(reactQueryHooks)
  const { put } = useConsoleBackendClient()
  const queryClient = useQueryClient()

  return useMutation(
    [MutationKeys.UpdateRole],
    (body) =>
      put({
        body,
        path: `update-role`,
      }),
    {
      ...mutationOptions,
      onSuccess: (data, context) => {
        queryClient.removeQueries([QueryKeys.ListTenantMembers])
        mutationOptions?.onSuccess?.(data, context)
      },
    }
  )
}

export const useRoles = () => {
  const { get } = useConsoleBackendClient()
  return useQuery(
    [QueryKeys.RolesData],
    () => get<GetRolesResponse>({ path: `roles` }),
    reactQueryDefaultOptions
  )
}
